/**
 * Authority Management Component - View and manage authorities and employees
 *
 * Allows viewing authority details, managing employees, and triggering key rotation.
 */

import { useState, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Divider,
  Tooltip,
} from '@mui/material';
import {
  PersonAdd as PersonAddIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  ContentCopy as CopyIcon,
  History as HistoryIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useCryptoKeyStore, createEmployeeGKDKSlot } from '@antragsmanager/crypto-lib';
import {
  getAuthorityWithEmployees,
  revokeEmployee,
  rotateAuthorityKey,
  getAuthorityAuditLog,
  AuthorityEmployeeDto,
  storeGKDKDistributions,
} from '../../api/keysApi';
import { EmployeeOnboarding } from './EmployeeOnboarding';

interface AuthorityManagementProps {
  authorityId: string;
}

export function AuthorityManagement({ authorityId }: AuthorityManagementProps) {
  const [onboardingOpen, setOnboardingOpen] = useState(false);
  const [revokeDialogOpen, setRevokeDialogOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState<AuthorityEmployeeDto | null>(null);
  const [revokeReason, setRevokeReason] = useState('');
  const [auditLogOpen, setAuditLogOpen] = useState(false);

  const { activeKey } = useCryptoKeyStore();
  const queryClient = useQueryClient();

  // Fetch authority details
  const {
    data: authorityData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['authority', authorityId],
    queryFn: () => getAuthorityWithEmployees(authorityId),
    enabled: !!authorityId,
  });

  // Fetch audit log
  const { data: auditLog } = useQuery({
    queryKey: ['authority-audit', authorityId],
    queryFn: () => getAuthorityAuditLog(authorityId, 20),
    enabled: auditLogOpen,
  });

  // Revoke employee mutation with GKDK distribution
  const revokeMutation = useMutation({
    mutationFn: async ({
      employeeKeyId,
      reason,
    }: {
      employeeKeyId: string;
      reason: string;
    }) => {
      if (!activeKey) throw new Error('No active key');

      // Revoke employee - this triggers rotation on the backend
      const response = await revokeEmployee(authorityId, employeeKeyId, {
        reason,
        revokedByKeyId: activeKey.keyId,
      });

      // Rotate locally to create new GKDK slot for the new epoch
      const newBundle = await rotateAuthority(authorityId);

      // Get fresh employee list (excluding the revoked employee)
      const employeesData = await getAuthorityWithEmployees(authorityId);
      const activeEmployees = employeesData.employees.filter(
        (e) => e.isActive && e.keyId !== activeKey.keyId
      );

      // Create and store GKDK distributions for all other active employees
      if (activeEmployees.length > 0) {
        const distributions = await Promise.all(
          activeEmployees.map(async (employee) => {
            const slot = await createEmployeeGKDKSlot(
              newBundle,
              employee.publicKeyJwk,
              activeKey
            );
            return {
              employeeKeyId: employee.keyId,
              wrappedBundle: {
                wrappedBundle: slot.wrappedBundle,
                iv: slot.iv,
                wrappedByPublicKey: slot.wrappedByPublicKey,
                authorityName: slot.authorityName,
              },
            };
          })
        );

        // Store distributions on backend for other employees to fetch
        await storeGKDKDistributions(authorityId, {
          epoch: response.newEpoch,
          distributions,
          createdByKeyId: activeKey.keyId,
        });
      }

      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['authority', authorityId] });
      setRevokeDialogOpen(false);
      setSelectedEmployee(null);
      setRevokeReason('');
    },
  });

  // Get rotateAuthority from crypto store
  const { rotateAuthority } = useCryptoKeyStore();

  // Rotate key mutation with GKDK distribution
  const rotateMutation = useMutation({
    mutationFn: async () => {
      console.log('[Rotation] Starting rotation mutation');
      if (!activeKey) throw new Error('No active key');

      // First, trigger backend rotation to increment epoch
      console.log('[Rotation] Step 1: Calling backend rotateAuthorityKey');
      const response = await rotateAuthorityKey(authorityId, activeKey.keyId);
      console.log('[Rotation] Step 1 complete, newEpoch:', response.newEpoch);

      // Then, rotate locally to create new GKDK slot for the new epoch
      console.log('[Rotation] Step 2: Calling local rotateAuthority');
      const newBundle = await rotateAuthority(authorityId);
      console.log('[Rotation] Step 2 complete, bundle epoch:', newBundle.epoch);

      // Get fresh employee list
      console.log('[Rotation] Step 3: Getting employee list');
      const employeesData = await getAuthorityWithEmployees(authorityId);
      const activeEmployees = employeesData.employees.filter(
        (e) => e.isActive && e.keyId !== activeKey.keyId
      );
      console.log('[Rotation] Step 3 complete, other active employees:', activeEmployees.length);

      // Create and store GKDK distributions for all other employees
      if (activeEmployees.length > 0) {
        console.log('[Rotation] Step 4: Creating GKDK distributions');
        const distributions = await Promise.all(
          activeEmployees.map(async (employee) => {
            console.log('[Rotation] Creating slot for employee:', employee.keyId);
            console.log('[Rotation] Employee publicKeyJwk:', JSON.stringify(employee.publicKeyJwk));
            try {
              const slot = await createEmployeeGKDKSlot(
                newBundle,
                employee.publicKeyJwk,
                activeKey
              );
              console.log('[Rotation] Slot created for employee:', employee.keyId);
              return {
                employeeKeyId: employee.keyId,
                wrappedBundle: {
                  wrappedBundle: slot.wrappedBundle,
                  iv: slot.iv,
                  wrappedByPublicKey: slot.wrappedByPublicKey,
                  authorityName: slot.authorityName,
                },
              };
            } catch (slotError) {
              console.error('[Rotation] Failed to create slot for employee:', employee.keyId, slotError);
              throw slotError;
            }
          })
        );

        // Store distributions on backend for other employees to fetch
        console.log('[Rotation] Step 5: Storing distributions on backend');
        console.log('[Rotation] Request payload:', JSON.stringify({
          epoch: response.newEpoch,
          distributions: distributions.map(d => ({ employeeKeyId: d.employeeKeyId })),
          createdByKeyId: activeKey.keyId,
        }));
        try {
          await storeGKDKDistributions(authorityId, {
            epoch: response.newEpoch,
            distributions,
            createdByKeyId: activeKey.keyId,
          });
          console.log('[Rotation] Step 5 complete');
        } catch (apiError) {
          console.error('[Rotation] Step 5 failed:', apiError);
          throw apiError;
        }
      }

      console.log('[Rotation] All steps complete!');
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['authority', authorityId] });
    },
    onError: (error) => {
      console.error('Rotation failed:', error);
    },
  });

  const handleRevokeClick = useCallback((employee: AuthorityEmployeeDto) => {
    setSelectedEmployee(employee);
    setRevokeDialogOpen(true);
  }, []);

  const handleRevokeConfirm = useCallback(() => {
    if (selectedEmployee && revokeReason) {
      revokeMutation.mutate({
        employeeKeyId: selectedEmployee.keyId,
        reason: revokeReason,
      });
    }
  }, [selectedEmployee, revokeReason, revokeMutation]);

  const handleCopyFingerprint = useCallback((fingerprint: string) => {
    navigator.clipboard.writeText(fingerprint);
  }, []);

  const formatFingerprint = (fingerprint: string) => {
    return fingerprint.match(/.{1,4}/g)?.join(' ') || fingerprint;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('de-DE');
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !authorityData) {
    return (
      <Alert severity="error">
        Fehler beim Laden der Behörde. {error instanceof Error ? error.message : ''}
      </Alert>
    );
  }

  const { authority, employees } = authorityData;
  const isCurrentUserEmployee = employees.some(
    (e) => e.keyId === activeKey?.keyId && e.isActive
  );

  return (
    <Box>
      {/* Authority Info Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box>
              <Typography variant="h5" gutterBottom>
                {authority.authorityName}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <Chip
                  label={authority.isActive ? 'Aktiv' : 'Deaktiviert'}
                  color={authority.isActive ? 'success' : 'error'}
                  size="small"
                />
                <Chip
                  label={`Epoche ${authority.currentEpoch}`}
                  variant="outlined"
                  size="small"
                />
                <Chip
                  label={`${authority.activeEmployeeCount} Mitarbeiter`}
                  variant="outlined"
                  size="small"
                />
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Tooltip title="Audit-Log anzeigen">
                <IconButton onClick={() => setAuditLogOpen(true)}>
                  <HistoryIcon />
                </IconButton>
              </Tooltip>
              {isCurrentUserEmployee && (
                <>
                  <Button
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={() => rotateMutation.mutate()}
                    disabled={rotateMutation.isPending}
                  >
                    Schlüssel rotieren
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<PersonAddIcon />}
                    onClick={() => setOnboardingOpen(true)}
                  >
                    Mitarbeiter einladen
                  </Button>
                </>
              )}
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          <Typography variant="body2" color="text.secondary">
            <strong>Fingerprint:</strong>{' '}
            <code style={{ fontSize: '0.85em' }}>{formatFingerprint(authority.fingerprint)}</code>
            <IconButton
              size="small"
              onClick={() => handleCopyFingerprint(authority.fingerprint)}
              sx={{ ml: 1 }}
            >
              <CopyIcon fontSize="small" />
            </IconButton>
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <strong>Erstellt:</strong> {formatDate(authority.createdAt)}
          </Typography>
          {authority.lastRotationAt && (
            <Typography variant="body2" color="text.secondary">
              <strong>Letzte Rotation:</strong> {formatDate(authority.lastRotationAt)}
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Employees List */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Mitarbeiter
          </Typography>
          <List>
            {employees.filter((e) => e.isActive).map((employee) => (
              <ListItem key={employee.keyId} divider>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {employee.name}
                      {employee.keyId === activeKey?.keyId && (
                        <Chip label="Sie" size="small" color="primary" />
                      )}
                    </Box>
                  }
                  secondary={
                    <>
                      <Typography variant="body2" color="text.secondary">
                        Hinzugefügt: {formatDate(employee.addedAt)}
                        {employee.onboardedBy && ` (von ${employee.onboardedBy})`}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Epoche: {employee.lastKnownEpoch}
                      </Typography>
                    </>
                  }
                />
                <ListItemSecondaryAction>
                  {employee.keyId !== activeKey?.keyId && isCurrentUserEmployee && (
                    <Tooltip title="Mitarbeiter entfernen">
                      <IconButton
                        edge="end"
                        onClick={() => handleRevokeClick(employee)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>

          {employees.filter((e) => e.isActive).length === 0 && (
            <Typography color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
              Keine aktiven Mitarbeiter
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Onboarding Dialog */}
      <EmployeeOnboarding
        open={onboardingOpen}
        onClose={() => setOnboardingOpen(false)}
        authorityId={authorityId}
        authorityName={authority.authorityName}
        onComplete={() => queryClient.invalidateQueries({ queryKey: ['authority', authorityId] })}
      />

      {/* Revoke Confirmation Dialog */}
      <Dialog open={revokeDialogOpen} onClose={() => setRevokeDialogOpen(false)}>
        <DialogTitle>Mitarbeiter entfernen</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Möchten Sie {selectedEmployee?.name} wirklich aus dieser Behörde entfernen?
            Dies löst eine Schlüsselrotation aus, und der Mitarbeiter verliert den Zugriff
            auf neue verschlüsselte Daten.
          </DialogContentText>
          <TextField
            fullWidth
            label="Grund"
            value={revokeReason}
            onChange={(e) => setRevokeReason(e.target.value)}
            margin="normal"
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRevokeDialogOpen(false)}>Abbrechen</Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleRevokeConfirm}
            disabled={!revokeReason || revokeMutation.isPending}
          >
            {revokeMutation.isPending ? <CircularProgress size={24} /> : 'Entfernen'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Audit Log Dialog */}
      <Dialog
        open={auditLogOpen}
        onClose={() => setAuditLogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Audit-Log</DialogTitle>
        <DialogContent>
          <List>
            {auditLog?.map((entry, index) => (
              <ListItem key={index} divider>
                <ListItemText
                  primary={entry.operation}
                  secondary={
                    <>
                      <Typography variant="body2" color="text.secondary">
                        Von: {entry.performedByKeyId}
                        {entry.targetKeyId && ` | Ziel: ${entry.targetKeyId}`}
                        {entry.epoch && ` | Epoche: ${entry.epoch}`}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {formatDate(entry.timestamp)}
                      </Typography>
                    </>
                  }
                />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAuditLogOpen(false)}>Schließen</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
